import React, { useState, useEffect } from 'react';
import { apiFetch, useAuth } from '../../App';
import { CalendarDays, DollarSign, Users, MapPin, Ticket, TrendingUp, ArrowUp, ArrowDown } from 'lucide-react';

export default function DenDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/admin/stats').then(s => { setStats(s); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      {[1,2].map(i => <div key={i} className="skeleton h-32 rounded-2xl" />)}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
    </div>
  );

  return (
    <div data-testid="den-dashboard">
      <div className="mb-8">
        <h1 className="font-outfit text-2xl sm:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Dashboard</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Welcome back, {user?.name}. Here's your overview.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Bookings', value: stats?.totalBookings || 0, icon: CalendarDays, color: '#FF8C42', change: '+12%' },
          { label: 'Revenue', value: `₹${(stats?.totalRevenue || 0).toLocaleString()}`, icon: DollarSign, color: '#22C55E', change: '+8%' },
          { label: 'Customers', value: stats?.totalCustomers || 0, icon: Users, color: '#8B5CF6', change: '+15%' },
          { label: 'Active Nodes', value: stats?.activeNodes || 0, icon: MapPin, color: '#20B2AA', change: '' },
        ].map((kpi, i) => (
          <div key={i} className="petfolk-card p-5" data-testid={`kpi-${kpi.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${kpi.color}15` }}>
                <kpi.icon size={20} style={{ color: kpi.color }} />
              </div>
              {kpi.change && (
                <span className="text-xs font-semibold text-green-600 flex items-center gap-0.5">
                  <ArrowUp size={12} /> {kpi.change}
                </span>
              )}
            </div>
            <p className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{kpi.value}</p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Bookings by Status */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="petfolk-card p-6" data-testid="bookings-by-status">
          <h3 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Bookings by Status</h3>
          <div className="space-y-3">
            {Object.entries(stats?.bookingsByStatus || {}).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <span className={`badge badge-${status.toLowerCase().replace('_', '-')}`}>{status.replace('_', ' ')}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                  <div className="h-full rounded-full bg-petfolk-orange" style={{ width: `${Math.min(100, (count / (stats?.totalBookings || 1)) * 100)}%` }} />
                </div>
                <span className="text-sm font-semibold w-8 text-right" style={{ color: 'var(--text-primary)' }}>{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="petfolk-card p-6" data-testid="quick-stats">
          <h3 className="font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255,140,66,0.05)' }}>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Today's Bookings</span>
              <span className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{stats?.todayBookings || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.05)' }}>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Completed</span>
              <span className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{stats?.completedBookings || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255,107,107,0.05)' }}>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Open Tickets</span>
              <span className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{stats?.openTickets || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(32,178,170,0.05)' }}>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Active Subscriptions</span>
              <span className="font-outfit font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{stats?.activeSubscriptions || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
